import { alpha, Stack } from '@mui/material';
import { Box } from '@mui/system';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { _getAdminIntroducers } from '@/api/introducer-dashboard/queries';
import type { AdminIntroducerItem } from '@/api/introducer-dashboard/types';
import SearchUserIcon from '@/assets/svg/ui-elements/search-user.svg';
import useDebounce from '@/global-hooks/use-debounce';
import { Avatar, Button, Input, theme, Typography } from '@/shared/design-system';
import { formatISOAsYearMonDay } from '@/utils/date';

import { StepProgress } from './step-progress';
import { CardContainer, FooterButtons, SearchResultItem, StepContainer } from './styled';
import type { SelectedIntroducer } from './types';

interface StepSelectIntroducerProps {
  selected: SelectedIntroducer | null;
  onSelect: (_introducer: SelectedIntroducer | null) => void;
  onNext: () => void;
  onClose: () => void;
}

export const StepSelectIntroducer = ({
  selected,
  onSelect,
  onNext,
  onClose,
}: StepSelectIntroducerProps) => {
  const [inputValue, setInputValue] = useState(selected?.name ?? '');
  const debouncedSearch = useDebounce(inputValue, 500);

  const showResults = !selected && debouncedSearch.trim().length >= 2;
  const showEmpty = !selected && debouncedSearch.trim().length < 2;

  const { data: searchData, isFetching } = useQuery({
    ..._getAdminIntroducers({ search: debouncedSearch.trim(), limit: 5 }),
    enabled: showResults,
  });

  const results: AdminIntroducerItem[] = searchData?.items ?? [];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (selected) {
      onSelect(null);
    }
  };

  const handleSelectItem = (item: AdminIntroducerItem) => {
    onSelect({
      id: item.id,
      name: item.name,
      email: item.email,
      joinedAt: item.joinedAt,
      referralsCount: item.referralsCount,
    });
    setInputValue(item.name);
  };

  return (
    <StepContainer>
      <StepProgress currentStep={1} />

      <Stack flexDirection="column" gap="8px">
        <Typography variant="body3">Introducer</Typography>
        <Input
          placeholder="Search for Introducer by name or email address"
          value={inputValue}
          onChange={handleInputChange}
        />
      </Stack>

      {showEmpty && (
        <Stack
          alignItems="center"
          flexDirection="column"
          gap="8px"
          padding="12px"
          borderRadius="12px"
          bgcolor={theme.colors.background.canvas}
        >
          <SearchUserIcon />
          <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.6)} align="center">
            Search for an introducer to begin
          </Typography>
        </Stack>
      )}

      {showResults && (
        <Stack gap="4px" flexDirection="column" maxHeight="250px" overflow="auto">
          {isFetching && (
            <Typography variant="body4" color="#818593">
              Searching…
            </Typography>
          )}
          {!isFetching && results.length === 0 && (
            <Stack alignItems="center" gap="12px" padding="12px" flexDirection="column">
              <SearchUserIcon />
              <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.6)}>
                No introducers found
              </Typography>
            </Stack>
          )}
          {results.map((item) => (
            <SearchResultItem key={item.id} onClick={() => handleSelectItem(item)}>
              <Avatar initials={item.name} />
              <Stack gap="2px" flexDirection="column">
                <Typography variant="body3" fontWeight={600} noWrap>
                  {item.name}
                </Typography>
                <Typography variant="body4" color={alpha(theme.colors.text.primary, 0.6)} noWrap>
                  {item.email}
                </Typography>
              </Stack>
            </SearchResultItem>
          ))}
        </Stack>
      )}

      {selected && (
        <CardContainer>
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between" gap="12px">
            <Stack flexDirection="row" alignItems="center" gap="12px" minWidth={0} flex={1}>
              <Avatar initials={selected.name} />
              <Stack gap="2px" flexDirection="column" maxWidth="200px">
                <Typography variant="body3" fontWeight={600} noWrap>
                  {selected.name}
                </Typography>
                <Typography variant="body4" color={alpha(theme.colors.text.primary, 0.6)} noWrap>
                  {selected.email}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap="12px">
            <Stack gap="2px" flexDirection="column">
              <Typography variant="body4" color={alpha(theme.colors.text.primary, 0.6)}>
                Joined
              </Typography>
              <Typography variant="body3">
                {formatISOAsYearMonDay(selected.joinedAt.split('T')[0])}
              </Typography>
            </Stack>

            <Stack gap="2px" flexDirection="column">
              <Typography variant="body4" color={alpha(theme.colors.text.primary, 0.6)}>
                Referrals
              </Typography>
              <Typography variant="body3">{selected.referralsCount}</Typography>
            </Stack>

            <Stack gap="2px" flexDirection="column">
              <Typography variant="body4" color={alpha(theme.colors.text.primary, 0.6)}>
                Eligible
              </Typography>
              <Typography variant="body3" color={theme.colors.text.brand}>
                Yes
              </Typography>
            </Stack>
          </Box>
        </CardContainer>
      )}

      <FooterButtons>
        <Button color="tertiary" variant="outline" onClick={onClose}>
          Back
        </Button>
        <Button color="primary" variant="solid" onClick={onNext} disabled={!selected}>
          Next
        </Button>
      </FooterButtons>
    </StepContainer>
  );
};
